import { normalizeAssetPath } from "./assetPaths";
import { getAssetEntries, type AssetEntry } from "../stores/assetStore";
import { getBytes } from "./idb";

export type ResolvedEnvironmentZipAsset = {
  sourcePath: string;
  zipPath: string;
  file: File;
};

type FileMap = Record<string, File>;
type AssetStoreMatch = {
  key: string;
  entry: AssetEntry;
};
type AssetStorePathCandidate = {
  path: string;
  isDirect: boolean;
};

function basenameOfPath(value: string): string {
  const normalized = normalizeAssetPath(value, {
    stripQueryHash: true,
    removeDotSegments: true,
    lowercase: true,
  });
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
}

function dirnameOfPath(value: string): string {
  const normalized = String(value || "").replace(/\\/g, "/");
  const idx = normalized.lastIndexOf("/");
  return idx >= 0 ? normalized.slice(0, idx) : "";
}

function joinZipPath(baseDir: string, relativePath: string): string {
  const rel = String(relativePath || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  if (!baseDir) return rel;
  return `${baseDir.replace(/\/+$/, "")}/${rel}`;
}

function toSafeZipPath(input: string): string {
  let p = String(input || "");
  p = p.replace(/\\/g, "/");
  p = p.replace(/^\/*/, "");
  p = p.replace(/:/g, "_");
  while (p.includes("../")) p = p.split("../").join("");
  while (p.includes("..\\")) p = p.split("..\\").join("");
  return p || "asset";
}

function sameFileIdentity(a: File, b: File): boolean {
  return a === b || (a.name === b.name && a.size === b.size && a.lastModified === b.lastModified);
}

function normalizedLookupPath(value: string): string {
  return normalizeAssetPath(value, {
    stripQueryHash: true,
    removeDotSegments: true,
    lowercase: true,
  });
}

function candidateAssetStorePaths(entry: AssetEntry): AssetStorePathCandidate[] {
  const candidates = new Map<string, AssetStorePathCandidate>();
  const add = (value: unknown, isDirect: boolean) => {
    if (typeof value !== "string" || !value.trim()) return;
    const normalized = normalizedLookupPath(value);
    if (!normalized) return;
    const existing = candidates.get(normalized);
    if (!existing || isDirect) candidates.set(normalized, { path: normalized, isDirect });
    if (!normalized.includes("/")) {
      const alias = `assets/${normalized}`;
      if (!candidates.has(alias)) candidates.set(alias, { path: alias, isDirect: false });
    }
  };

  add(entry.name, true);
  add(entry.sources?.url, false);
  add(entry.sources?.blobUrl, false);

  const entryName = typeof entry.name === "string" ? entry.name.replace(/^\/+/, "") : "";
  const libraryPrefix =
    typeof entry.sources?.libraryPathPrefix === "string"
      ? entry.sources.libraryPathPrefix.replace(/^\/+|\/+$/g, "")
      : "";
  if (entryName && libraryPrefix) {
    add(`${libraryPrefix}/${entryName}`, false);
    if (!entryName.includes("/")) add(`${libraryPrefix}/assets/${entryName}`, false);
  }

  return Array.from(candidates.values());
}

function isCatalogAssetPath(normalizedPath: string): boolean {
  return normalizedPath.startsWith("robots/") || normalizedPath.startsWith("objects/");
}

function preferLatestCatalogMatch(
  matches: AssetStoreMatch[],
  normalizedPath: string
): AssetStoreMatch[] {
  if (matches.length <= 1 || !isCatalogAssetPath(normalizedPath)) return matches;
  const sorted = [...matches].sort((a, b) => {
    const aTime = a.entry.updatedAt ?? a.entry.createdAt ?? 0;
    const bTime = b.entry.updatedAt ?? b.entry.createdAt ?? 0;
    if (aTime !== bTime) return bTime - aTime;
    return b.key.localeCompare(a.key);
  });
  return sorted[0] ? [sorted[0]] : matches;
}

export function resolveAssetStoreEntryForPath(requestedPath: string): AssetStoreMatch | null {
  const normalizedPath = normalizedLookupPath(requestedPath);
  if (!normalizedPath) return null;
  const basename = basenameOfPath(normalizedPath);
  const directExactMatches: AssetStoreMatch[] = [];
  const aliasExactMatches: AssetStoreMatch[] = [];
  const directBasenameMatches: AssetStoreMatch[] = [];
  const aliasBasenameMatches: AssetStoreMatch[] = [];

  Object.entries(getAssetEntries() || {}).forEach(([key, entry]) => {
    if (!entry || (entry.kind !== "mesh" && entry.kind !== "texture")) return;
    const candidates = candidateAssetStorePaths(entry);
    const exactCandidate = candidates.find(
      (candidate) =>
        candidate.path === normalizedPath ||
        candidate.path.endsWith(`/${normalizedPath}`) ||
        (normalizedPath.startsWith("assets/") &&
          candidate.path === normalizedPath.replace(/^assets\//, ""))
    );
    if (exactCandidate) {
      (exactCandidate.isDirect ? directExactMatches : aliasExactMatches).push({ key, entry });
      return;
    }
    if (
      candidates.some(
        (candidate) => candidate.isDirect && basenameOfPath(candidate.path) === basename
      )
    ) {
      directBasenameMatches.push({ key, entry });
      return;
    }
    if (candidates.some((candidate) => basenameOfPath(candidate.path) === basename)) {
      aliasBasenameMatches.push({ key, entry });
    }
  });

  const uniqueDirectExact = preferLatestCatalogMatch(
    collapseEquivalentAssetStoreMatches(directExactMatches),
    normalizedPath
  );
  if (uniqueDirectExact.length === 1) return uniqueDirectExact[0];
  if (uniqueDirectExact.length > 1) {
    throw new Error(
      `Environment export: ambiguous asset reference "${requestedPath}" matched multiple registered assets.`
    );
  }

  const uniqueAliasExact = preferLatestCatalogMatch(
    collapseEquivalentAssetStoreMatches(aliasExactMatches),
    normalizedPath
  );
  if (uniqueAliasExact.length === 1) return uniqueAliasExact[0];
  if (uniqueAliasExact.length > 1) {
    throw new Error(
      `Environment export: ambiguous asset reference "${requestedPath}" matched multiple registered assets.`
    );
  }

  const uniqueDirectBasename = preferLatestCatalogMatch(
    collapseEquivalentAssetStoreMatches(directBasenameMatches),
    normalizedPath
  );
  if (uniqueDirectBasename.length === 1) return uniqueDirectBasename[0];
  if (uniqueDirectBasename.length > 1) {
    throw new Error(
      `Environment export: ambiguous asset basename "${requestedPath}" matched multiple registered assets.`
    );
  }

  const uniqueAliasBasename = preferLatestCatalogMatch(
    collapseEquivalentAssetStoreMatches(aliasBasenameMatches),
    normalizedPath
  );
  if (uniqueAliasBasename.length === 1) return uniqueAliasBasename[0];
  if (uniqueAliasBasename.length > 1) {
    throw new Error(
      `Environment export: ambiguous asset basename "${requestedPath}" matched multiple registered assets.`
    );
  }

  return null;
}

function assetStoreSourceIdentity(match: AssetStoreMatch): string {
  const sources = match.entry.sources || {};
  return (
    (typeof match.entry.hash === "string" && match.entry.hash ? `hash:${match.entry.hash}` : "") ||
    (typeof sources.idbKey === "string" && sources.idbKey ? `idb:${sources.idbKey}` : "") ||
    (typeof sources.url === "string" && sources.url ? `url:${sources.url}` : "") ||
    (typeof sources.blobUrl === "string" && sources.blobUrl ? `blob:${sources.blobUrl}` : "") ||
    `entry:${match.key}`
  );
}

function collapseEquivalentAssetStoreMatches(matches: AssetStoreMatch[]): AssetStoreMatch[] {
  const byIdentity = new Map<string, AssetStoreMatch>();
  matches.forEach((match) => {
    const identity = assetStoreSourceIdentity(match);
    if (!byIdentity.has(identity)) byIdentity.set(identity, match);
  });
  return Array.from(byIdentity.values());
}

async function fileFromAssetStoreEntry(
  match: AssetStoreMatch,
  zipPath: string
): Promise<File | null> {
  const sources = match.entry.sources || {};
  const fileName = basenameOfPath(zipPath) || basenameOfPath(match.entry.name || "") || "asset";

  if (sources.idbKey) {
    try {
      const blob = await getBytes(sources.idbKey);
      if (blob) return new File([blob], fileName, { type: "application/octet-stream" });
    } catch {
      // Fall through to URL sources when persisted bytes are unavailable.
    }
  }

  const sourceUrl = sources.blobUrl || sources.url || "";
  if (!sourceUrl) return null;
  const response = await fetch(sourceUrl, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Environment export: failed to fetch registered asset "${sourceUrl}".`);
  }
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || "application/octet-stream" });
}

export async function resolveAssetStoreFileForPath(
  requestedPath: string,
  zipPath = requestedPath
): Promise<File | null> {
  const registered = resolveAssetStoreEntryForPath(requestedPath);
  if (!registered) return null;
  return fileFromAssetStoreEntry(registered, zipPath);
}

export async function resolveAssetStoreFileById(
  assetId: string,
  zipPath = assetId
): Promise<File | null> {
  const entry = getAssetEntries()?.[assetId];
  if (!entry || (entry.kind !== "mesh" && entry.kind !== "texture")) return null;
  return fileFromAssetStoreEntry({ key: assetId, entry }, zipPath);
}

function resolveFileFromMap(
  fileMap: FileMap,
  requestedPath: string
): { key: string; file: File } | null {
  const normalizedLookup = String(requestedPath || "")
    .trim()
    .replace(/\\/g, "/");
  if (!normalizedLookup) return null;

  const directKeys = [normalizedLookup, normalizedLookup.replace(/^\/+/, "")];
  for (const key of directKeys) {
    const direct = fileMap[key];
    if (direct instanceof File) return { key, file: direct };
  }

  const exact = new Map<string, string[]>();
  const byBase = new Map<string, string[]>();
  const addIndex = (map: Map<string, string[]>, key: string, value: string) => {
    if (!key) return;
    const existing = map.get(key) || [];
    existing.push(value);
    map.set(key, existing);
  };

  Object.keys(fileMap || {}).forEach((key) => {
    const normalized = normalizeAssetPath(key, {
      stripQueryHash: true,
      removeDotSegments: true,
      lowercase: true,
    });
    if (!normalized) return;
    addIndex(exact, normalized, key);
    addIndex(byBase, basenameOfPath(normalized), key);
  });

  const normalizedPath = normalizeAssetPath(requestedPath, {
    stripQueryHash: true,
    removeDotSegments: true,
    lowercase: true,
  });
  const candidates: string[] = [...(exact.get(normalizedPath) || [])];

  if (candidates.length === 0) {
    if (normalizedPath.startsWith("assets/")) {
      candidates.push(...(exact.get(normalizedPath.replace(/^assets\//, "")) || []));
    } else {
      candidates.push(...(exact.get(`assets/${normalizedPath}`) || []));
    }
  }

  if (candidates.length === 0) {
    candidates.push(...(byBase.get(basenameOfPath(normalizedPath)) || []));
  }

  const uniqueCandidates = Array.from(new Set(candidates)).filter(
    (key) => fileMap[key] instanceof File
  );
  if (uniqueCandidates.length === 0) return null;

  const firstKey = uniqueCandidates[0];
  const firstFile = fileMap[firstKey];
  const equivalent = uniqueCandidates.every((key) => sameFileIdentity(firstFile, fileMap[key]));
  if (equivalent) return { key: firstKey, file: firstFile };

  throw new Error(
    `Environment export: ambiguous asset reference "${requestedPath}" matched multiple files:\n${uniqueCandidates
      .slice(0, 10)
      .map((key) => `- ${key}`)
      .join("\n")}`
  );
}

function getCompilerDir(
  xmlDoc: Document,
  attr: "meshdir" | "texturedir",
  fallback: string
): string {
  const raw = xmlDoc.querySelector("compiler")?.getAttribute(attr);
  return String(raw || fallback)
    .replace(/\\/g, "/")
    .replace(/^\/+|\/+$/g, "");
}

export function resolveMjcfAssetZipPath(fileAttr: string, compilerDir: string): string {
  const filePath = String(fileAttr || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  if (!filePath) return "";
  if (compilerDir && filePath !== compilerDir && !filePath.startsWith(`${compilerDir}/`)) {
    return toSafeZipPath(`${compilerDir}/${filePath}`);
  }
  return toSafeZipPath(filePath);
}

async function collectObjSidecars(asset: ResolvedEnvironmentZipAsset, fileMap: FileMap) {
  if (!asset.zipPath.toLowerCase().endsWith(".obj")) return [];
  let text;
  try {
    text = await asset.file.text();
  } catch {
    return [];
  }

  const sidecars: ResolvedEnvironmentZipAsset[] = [];
  const objDir = dirnameOfPath(asset.zipPath);
  for (const line of text.split(/\r?\n/)) {
    const match = line.match(/^\s*mtllib\s+(.+?)\s*$/i);
    if (!match?.[1]) continue;
    for (const rawName of match[1].split(/\s+/).filter(Boolean)) {
      const zipPath = toSafeZipPath(joinZipPath(objDir, rawName));
      const resolved = resolveFileFromMap(fileMap, zipPath) || resolveFileFromMap(fileMap, rawName);
      if (resolved) {
        sidecars.push({ sourcePath: resolved.key, zipPath, file: resolved.file });
      }
    }
  }
  return sidecars;
}

export async function collectResolvedXmlAssetsForZip(
  xml: string,
  fileMap: FileMap
): Promise<ResolvedEnvironmentZipAsset[]> {
  const xmlDoc = new DOMParser().parseFromString(xml, "text/xml");
  const meshdir = getCompilerDir(xmlDoc, "meshdir", "assets");
  const texturedir = getCompilerDir(xmlDoc, "texturedir", "assets");
  const entries: ResolvedEnvironmentZipAsset[] = [];
  const byZipPathLower = new Map<string, ResolvedEnvironmentZipAsset>();

  const addAsset = async (zipPathRaw: string, lookupPathRaw?: string) => {
    const zipPath = toSafeZipPath(zipPathRaw);
    const lookupPath = lookupPathRaw
      ? String(lookupPathRaw).trim().replace(/\\/g, "/").replace(/^\/+/, "")
      : zipPath;
    if (!zipPath) return;

    const resolved =
      resolveFileFromMap(fileMap, lookupPath) || resolveFileFromMap(fileMap, zipPath);
    if (!resolved) {
      const registered =
        resolveAssetStoreEntryForPath(lookupPath) || resolveAssetStoreEntryForPath(zipPath);
      if (registered) {
        const file = await fileFromAssetStoreEntry(registered, zipPath);
        if (file) {
          const collisionKey = zipPath.toLowerCase();
          const existing = byZipPathLower.get(collisionKey);
          if (existing) {
            if (!sameFileIdentity(existing.file, file) || existing.zipPath !== zipPath) {
              throw new Error(
                `Environment export: duplicate zip asset path "${zipPath}" conflicts with "${existing.zipPath}".`
              );
            }
            return;
          }
          const asset = { sourcePath: registered.key, zipPath, file };
          byZipPathLower.set(collisionKey, asset);
          entries.push(asset);
          return;
        }
      }
    }
    if (!resolved) {
      throw new Error(`Environment export: missing asset referenced by XML: ${lookupPath}`);
    }

    const collisionKey = zipPath.toLowerCase();
    const existing = byZipPathLower.get(collisionKey);
    if (existing) {
      if (!sameFileIdentity(existing.file, resolved.file) || existing.zipPath !== zipPath) {
        throw new Error(
          `Environment export: duplicate zip asset path "${zipPath}" conflicts with "${existing.zipPath}".`
        );
      }
      return;
    }

    const asset = { sourcePath: resolved.key, zipPath, file: resolved.file };
    byZipPathLower.set(collisionKey, asset);
    entries.push(asset);
  };

  for (const meshEl of Array.from(xmlDoc.querySelectorAll("asset > mesh"))) {
    const fileAttr = meshEl.getAttribute("file") || meshEl.getAttribute("obj") || "";
    if (!fileAttr.trim()) continue;
    await addAsset(resolveMjcfAssetZipPath(fileAttr, meshdir), fileAttr);
  }

  for (const textureEl of Array.from(xmlDoc.querySelectorAll("asset > texture"))) {
    const fileAttr = textureEl.getAttribute("file") || "";
    if (!fileAttr.trim()) continue;
    await addAsset(resolveMjcfAssetZipPath(fileAttr, texturedir), fileAttr);
  }

  for (const sidecar of (
    await Promise.all(entries.map((entry) => collectObjSidecars(entry, fileMap)))
  ).flat()) {
    await addAsset(sidecar.zipPath, sidecar.sourcePath);
  }

  return entries.sort((a, b) => a.zipPath.localeCompare(b.zipPath));
}
